import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaTrophy, FaUsers, FaMoneyBillWave, FaCrown, FaPlay, FaChevronRight, FaFilePdf,
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import Navbar from '../components/Layout/Navbar';
import Avatar from '../components/UI/Avatar';
import LevelBadge from '../components/UI/LevelBadge';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { exportAllGroupsPDF, exportSingleGroupPDF } from '../utils/generateGroupsPDF';

const ROUND_LABELS = {
  round16:      '16e de Finale',
  quarterfinal: 'Quarts de Finale',
  semifinal:    'Demi-Finales',
  third_place:  'Petite Finale',
  final:        'FINALE',
};

const STATUS_MAP = {
  registration: { label: 'Inscriptions ouvertes', color: '#3b82f6' },
  group_stage:  { label: 'Phase de groupes',       color: '#f59e0b' },
  knockout:     { label: 'Élimination directe',    color: '#f97316' },
  finished:     { label: 'Terminé',                color: '#6b7280' },
};

// ── Composant poule ───────────────────────────────────────────────────────────
function GroupTable({ groupNum, players, currentUserId, champName }) {
  const sorted = [...players].sort((a, b) => (b.score || 0) - (a.score || 0));
  return (
    <div className="rounded-2xl overflow-hidden border border-green-900/40">
      <div className="flex items-center gap-2 px-3 py-2 bg-qfl-card">
        <span className="text-qfl-gold font-black text-sm flex-1" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
          GROUPE {groupNum}
        </span>
        <button
          onClick={() => exportSingleGroupPDF(groupNum, players, champName)}
          title={`Exporter le groupe ${groupNum} en PDF`}
          className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-red-400/70 hover:text-red-400 hover:bg-red-400/10 transition-colors text-xs font-bold"
          style={{ fontFamily: "'Rajdhani', sans-serif" }}>
          <FaFilePdf size={11} /> PDF
        </button>
      </div>
      {sorted.map((p, i) => {
        const isMe = p.id === currentUserId;
        const qual = i < 2 && p.score !== null;
        return (
          <div key={p.id}
            className={`flex items-center gap-3 px-3 py-2.5 border-t border-green-900/30
              ${isMe ? 'bg-qfl-gold/5' : 'hover:bg-white/3'} transition-colors`}>
            <span className={`w-5 text-center font-black text-sm ${qual ? 'text-qfl-gold' : 'text-white/30'}`}
              style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              {i + 1}
            </span>
            <Avatar user={p} size="xs" />
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-bold truncate ${isMe ? 'text-qfl-gold' : 'text-white'}`}
                style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                {p.pseudo} {isMe && <span className="text-qfl-gold/60 text-xs">(Vous)</span>}
              </p>
            </div>
            <div className="text-right">
              {p.score !== null ? (
                <span className="text-white font-black text-sm"
                  style={{ fontFamily: "'Rajdhani', sans-serif" }}>{p.score}</span>
              ) : (
                <span className="text-white/20 text-xs">—</span>
              )}
            </div>
            {qual && (
              <span className="text-green-400 text-xs font-bold" title="Qualifié">✓</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Composant match éliminatoire ──────────────────────────────────────────────
function MatchCard({ match, currentUserId, onPlayMatch, playing }) {
  const isP1    = match.player1_id === currentUserId;
  const isP2    = match.player2_id === currentUserId;
  const myScore = isP1 ? match.player1_score : isP2 ? match.player2_score : null;
  const canPlay = (isP1 || isP2) && myScore === null && match.status !== 'completed' && match.status !== 'bye';

  return (
    <div className="rounded-xl border border-white/10 overflow-hidden bg-black/20">
      {/* Joueur 1 */}
      {['player1', 'player2'].map((slot) => {
        const pid    = match[`${slot}_id`];
        const pseudo = match[`${slot}_pseudo`];
        const avatar = match[`${slot}_avatar`];
        const sc     = match[`${slot}_score`];
        const isWinner = match.winner_id === pid && match.status === 'completed';
        const isMe   = pid === currentUserId;

        return (
          <div key={slot}
            className={`flex items-center gap-3 px-3 py-2.5
              ${slot === 'player1' ? 'border-b border-white/5' : ''}
              ${isWinner ? 'bg-qfl-gold/8' : ''}`}>
            {pid ? (
              <>
                <Avatar user={{ pseudo, avatar_url: avatar }} size="xs" />
                <span className={`flex-1 text-sm font-bold truncate ${isMe ? 'text-qfl-gold' : 'text-white'}`}
                  style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                  {pseudo} {isMe && <span className="text-xs text-qfl-gold/60">(Vous)</span>}
                </span>
                <span className={`font-black text-sm ${isWinner ? 'text-qfl-gold' : 'text-white/60'}`}
                  style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                  {sc !== null ? sc : '—'}
                </span>
                {isWinner && <span>🏆</span>}
              </>
            ) : (
              <span className="text-white/20 text-xs italic">En attente...</span>
            )}
          </div>
        );
      })}

      {/* Bouton jouer */}
      {canPlay && (
        <button
          onClick={() => onPlayMatch(match)}
          disabled={playing === match.id}
          className="w-full py-2 flex items-center justify-center gap-2 font-black text-xs uppercase tracking-widest transition-opacity disabled:opacity-50"
          style={{
            fontFamily: "'Rajdhani', sans-serif",
            background: 'linear-gradient(135deg, #f0b429, #c97d08)',
            color: '#0d2b1a',
          }}>
          {playing === match.id
            ? <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            : <><FaPlay size={10} /> JOUER CE MATCH</>}
        </button>
      )}
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────────────────────
function ChampionshipDetail() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const { user }  = useAuth();
  const { isConnected } = useSocket();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState('groups'); // 'groups' | 'bracket'
  const [playing, setPlaying] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/championships/${id}`)
      .then(({ data: d }) => {
        setData(d);
        // Choisir l'onglet par défaut selon le statut
        if (d.championship.status === 'knockout' || d.championship.status === 'finished') {
          setTab('bracket');
        }
      })
      .catch(() => navigate('/championships'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]);

  const handlePlayMatch = async (match) => {
    if (!isConnected) {
      toast.error('Connexion temps réel requise pour jouer');
      return;
    }
    setPlaying(match.id);
    try {
      const { data: d } = await api.post(`/championships/${id}/matches/${match.id}/start-duel`);
      navigate(`/duel?gameId=${d.gameId}&champMatchId=${match.id}&champId=${id}`);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Impossible de lancer le match');
      setPlaying(null);
    }
  };

  if (loading) {
    return (
      <div className="app-container flex items-center justify-center min-h-screen">
        <div className="w-10 h-10 rounded-full border-4 border-qfl-gold border-t-transparent animate-spin" />
      </div>
    );
  }

  const { championship: c, participants, matches } = data;
  const s = STATUS_MAP[c.status] || STATUS_MAP.registration;

  // Organiser participants par groupe
  const groupMap = {};
  for (const p of participants) {
    const g = p.group_number || 0;
    if (!groupMap[g]) groupMap[g] = [];
    groupMap[g].push(p);
  }

  // Organiser matches par round
  const matchByRound = {};
  for (const m of matches) {
    if (!matchByRound[m.round]) matchByRound[m.round] = [];
    matchByRound[m.round].push(m);
  }

  const roundOrder = ['round16', 'quarterfinal', 'semifinal', 'third_place', 'final'];

  return (
    <div className="app-container flex flex-col min-h-screen" style={{ paddingBottom: 80 }}>
      <Navbar showBack onBack={() => navigate(user?.role === 'admin' ? '/admin/championships' : '/championships')} />

      <div className="px-4 pt-4">
        {/* Header */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-xl font-black text-white uppercase tracking-widest flex-1 truncate"
              style={{ fontFamily: "'Rajdhani', sans-serif" }}>{c.nom}</h1>
            {c.premiumOnly && <FaCrown size={14} className="text-qfl-gold" />}
          </div>
          <div className="flex items-center gap-2 mb-2">
            <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
            <span className="text-xs font-semibold" style={{ color: s.color }}>{s.label}</span>
            <span className="text-white/20 text-xs">·</span>
            <span className="text-white/40 text-xs flex items-center gap-1">
              <FaUsers size={9} /> {c.participantCount}/{c.maxPlayers}
            </span>
          </div>

          {/* Prizes */}
          {(c.prizeMoney || c.prizeCoins > 0) && (
            <div className="flex items-center gap-3 p-3 rounded-xl mb-3"
              style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.15)' }}>
              <FaMoneyBillWave size={16} className="text-green-400" />
              <div>
                {c.prizeMoney && (
                  <p className="text-green-400 font-black text-base leading-tight"
                    style={{ fontFamily: "'Rajdhani', sans-serif" }}>{c.prizeMoney}</p>
                )}
                {c.prizeDescription && (
                  <p className="text-white/40 text-xs">{c.prizeDescription}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Onglets */}
        {c.status !== 'registration' && (
          <div className="flex gap-2 mb-4">
            {['groups', 'bracket'].map((t) => (
              <button key={t} onClick={() => setTab(t)}
                className={`px-4 py-1.5 rounded-xl text-xs font-black uppercase tracking-widest transition-colors ${
                  tab === t
                    ? 'bg-qfl-gold text-qfl-dark'
                    : 'bg-white/10 text-white/50 hover:bg-white/15'
                }`}
                style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                {t === 'groups' ? 'Poules' : 'Bracket'}
              </button>
            ))}
          </div>
        )}

        {/* Bannière quiz phase de groupes */}
        {c.status === 'group_stage' && (() => {
          const me = participants.find((p) => p.id === user?.id);
          if (!me) return null;
          const hasPlayed = me.score !== null && me.score !== undefined;
          return (
            <div className="mb-4">
              {hasPlayed ? (
                <div className="flex items-center gap-3 p-3 rounded-2xl"
                  style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)' }}>
                  <span className="text-green-400 text-xl">✅</span>
                  <div className="flex-1">
                    <p className="text-green-400 font-black text-sm uppercase tracking-widest"
                      style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                      Quiz de groupe joué
                    </p>
                    <p className="text-white/50 text-xs">
                      Ton score : <strong className="text-white">{me.score} pts</strong>
                      {' · '}{me.correct_answers}/{20} bonnes réponses
                    </p>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => navigate(`/championships/${c.id}/quiz`)}
                  className="w-full py-4 rounded-2xl font-black text-base uppercase tracking-widest flex items-center justify-center gap-3 transition-all active:scale-95"
                  style={{
                    fontFamily: "'Rajdhani', sans-serif",
                    background: 'linear-gradient(135deg, #f0b429, #c97d08)',
                    color: '#0d2b1a',
                    boxShadow: '0 4px 20px rgba(240,180,41,0.35)',
                  }}>
                  <span className="text-xl">⚔️</span>
                  JOUER MA PHASE DE GROUPES
                </button>
              )}
            </div>
          );
        })()}

        {/* Contenu onglet Poules */}
        {(tab === 'groups' || c.status === 'registration') && (
          <div>
            {c.status === 'registration' ? (
              <div className="text-center py-10">
                <FaUsers size={32} className="text-white/20 mx-auto mb-3" />
                <p className="text-white/40 text-sm font-bold"
                  style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                  {c.participantCount} joueur(s) inscrit(s) · {c.maxPlayers - c.participantCount} place(s) restante(s)
                </p>
                <p className="text-white/25 text-xs mt-1">Les groupes seront tirés au sort par l'admin</p>
                <div className="mt-4 flex flex-col gap-2 max-w-xs mx-auto">
                  {participants.map((p) => (
                    <div key={p.id} className="flex items-center gap-3 p-2 rounded-xl bg-white/5">
                      <Avatar user={p} size="xs" />
                      <span className="text-white text-sm font-bold"
                        style={{ fontFamily: "'Rajdhani', sans-serif" }}>{p.pseudo}</span>
                      <LevelBadge level={p.level} size="xs" />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {/* Bouton export toutes les poules */}
                <button
                  onClick={() => exportAllGroupsPDF(groupMap, c.nom)}
                  className="flex items-center justify-center gap-2 py-2.5 rounded-xl font-black text-sm uppercase tracking-widest transition-colors"
                  style={{
                    fontFamily: "'Rajdhani', sans-serif",
                    background: 'rgba(239,68,68,0.1)',
                    border: '1px solid rgba(239,68,68,0.25)',
                    color: '#f87171',
                  }}>
                  <FaFilePdf size={13} /> EXPORTER TOUTES LES POULES — PDF
                </button>

                {Object.entries(groupMap)
                  .filter(([g]) => g !== '0')
                  .sort(([a], [b]) => parseInt(a) - parseInt(b))
                  .map(([g, players]) => (
                    <GroupTable
                      key={g}
                      groupNum={g}
                      players={players}
                      currentUserId={user?.id}
                      champName={c.nom}
                    />
                  ))}
              </div>
            )}
          </div>
        )}

        {/* Contenu onglet Bracket */}
        {tab === 'bracket' && (
          <div className="flex flex-col gap-6">
            {roundOrder.map((round) => {
              const roundMatches = matchByRound[round];
              if (!roundMatches || roundMatches.length === 0) return null;
              return (
                <div key={round}>
                  <p className={`text-xs font-black uppercase tracking-widest mb-3 ${
                    round === 'final' ? 'text-qfl-gold' : 'text-white/50'
                  }`} style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                    {round === 'final' && '⭐ '}{ROUND_LABELS[round]}
                  </p>
                  <div className="grid grid-cols-1 gap-3">
                    {roundMatches.map((m) => (
                      <MatchCard
                        key={m.id}
                        match={m}
                        currentUserId={user?.id}
                        onPlayMatch={handlePlayMatch}
                        playing={playing}
                      />
                    ))}
                  </div>
                </div>
              );
            })}

            {matches.length === 0 && (
              <div className="text-center py-10 text-white/30 text-sm">
                Le bracket sera disponible après la phase de groupes.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ChampionshipDetail;
